import { Link, useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  CircleCheck,
  CloudUpload,
  Copy,
  ImageOff,
  Package,
  PackageOpen,
  Gauge,
  Pencil,
  QrCode,
  Stamp,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { mouldStatusLabel } from '@mouldtrack/shared';
import { Badge, Card, CardBody, CardHeader, CardTitle, Code, EmptyState, STATUS_TONE, Skeleton, Tooltip } from '../../components/ui/primitives.js';
import { ConfirmDialog } from '../../components/ui/dialog.js';
import { hasOwnName, useMould } from '../../db/queries.js';
import { useCan } from '../../auth/AuthProvider.js';
import {
  DESIGN_A_CODE,
  DESIGN_B_CODE,
  DESIGN_LABEL,
  NameplateCompareDialog,
} from './NameplateCompareDialog.js';
import { deleteRow, updateRow } from '../../db/mutations.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../../components/ui/menu.js';
import { ApiClientError, downloadBlob, isNetworkError } from '../../api/client.js';
import { MouldImports } from '../imports/MouldImports.js';
import { placeOf, useMouldLocations, whereabouts } from './useMouldLocations.js';
import { describeDownload, downloadDate, notifyEngraveDownloaded, useEngraveDownloads, useOfferedDesigns } from './useEngrave.js';
import { mouldQrValue } from '../../lib/qr.js';
import { MOULD_SECTIONS, displayKey, type FieldDef } from './fields.js';
import { ShotReadingDialog } from './ShotReadingDialog.js';
import { MouldLabelDialog } from './MouldLabelDialog.js';
import { MouldHistory } from './MouldHistory.js';
import { MouldAuditTrail } from './MouldAuditTrail.js';
import { MouldPhotoGallery } from './MouldPhotoGallery.js';
import { MouldJourneyCard } from '../journeys/MouldJourneyCard.js';
import { formatDate } from '../../lib/format.js';

/**
 * The mould profile.
 *
 * This is also the page a QR scan will land on in week 3, so it is built to be
 * read on a phone held at arm's length in a die store: identity first, specs
 * second, everything legible without zooming.
 */
export function MouldDetailPage() {
  const { assetCode } = useParams();
  const navigate = useNavigate();
  const mould = useMould(assetCode);
  const canEdit = useCan('update', 'moulds');
  const canDelete = useCan('delete', 'moulds');
  const canExportNameplate = useCan('export', 'nameplates');
  const canRecordShots = useCan('update', 'moulds');
  const [recordingShots, setRecordingShots] = useState(false);
  const [engraving, setEngraving] = useState<'svg' | 'dxf' | null>(null);
  /*
   * Which plate this mould gets.
   *
   * Held here rather than written to the record: choosing between two designs
   * means looking at both for the same tool, and setting the mould's template
   * in order to find out what it looks like is a change to the record made to
   * ask a question. It is saved only when somebody presses Use this one.
   */
  // The design's code, not its name: the code is what the nameplate endpoint takes.
  // Design B is the default (8 Oct 2026) until someone picks another for this mould.
  const [chosenDesign, setDesign] = useState<string>(() => mould?.nameplateTemplateCode ?? DESIGN_B_CODE);
  // Only designs the server offers (Design B alone by default): a mould whose
  // own design is not offered - Design A while it is hidden - shows and
  // engraves Design B, as the server draws it.
  const offeredDesigns = useOfferedDesigns();
  const design = offeredDesigns.some((d) => d.code === chosenDesign) ? chosenDesign : (offeredDesigns[0]?.code ?? DESIGN_B_CODE);
  const engraveDownloads = useEngraveDownloads();
  // The mould arrives after the first render: take its own design once it does.
  const designFor = useRef<string | null>(null);
  useEffect(() => {
    if (!mould || designFor.current === mould.id) return;
    designFor.current = mould.id;
    setDesign(mould.nameplateTemplateCode ?? DESIGN_B_CODE);
  }, [mould]);
  const [comparing, setComparing] = useState(false);
  const [engraveError, setEngraveError] = useState<string | null>(null);

  async function downloadNameplate(format: 'svg' | 'dxf') {
    if (!mould) return;
    setEngraving(format);
    setEngraveError(null);
    try {
      const blob = await downloadBlob(
        `/moulds/${encodeURIComponent(mould.assetCode)}/nameplate?format=${format}&design=${encodeURIComponent(design)}`,
      );
      // Object URL, then click, then revoke on a timer: revoking straight away
      // cancels the download in some browsers.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nameplate-${mould.assetCode}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      // The DXF is the engrave file: the server recorded it, so show the tick.
      if (format === 'dxf') notifyEngraveDownloaded();
    } catch (e) {
      setEngraveError(
        e instanceof ApiClientError
          ? e.message
          : isNetworkError(e)
            ? 'No connection. The nameplate is rendered on the server.'
            : 'Could not produce the nameplate.',
      );
    } finally {
      setEngraving(null);
    }
  }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [openPhotos, setOpenPhotos] = useState(0);
  // Where it is now, from the scan ledger - what someone scanning the plate wants first.
  const places = useMouldLocations();

  if (mould === undefined) return <DetailSkeleton />;

  if (mould === null) {
    return (
      <Page title="Mould not found" back={{ to: '/moulds', label: 'Back to catalog' }}>
        <Card>
          <EmptyState
            icon={<Package />}
            title={`No mould with ID "${assetCode}"`}
            description="It may have been removed, or this device has not finished downloading the catalog."
          />
        </Card>
      </Page>
    );
  }

  const place = placeOf(places, mould);
  const where = whereabouts(mould, place);
  // Every detail the plate carries. Every label is engraved; a blank detail is
  // engraved blank - it never stops the plate being made.
  const ownerName = mould.ownerCompanyName;
  // A blank detail is shown as blank, with a way to fill it in: an import fills
  // what the sheet has, and people add the rest later.
  const addHref = (field: string) =>
    canEdit ? `/moulds/${encodeURIComponent(mould.assetCode)}/edit?field=${encodeURIComponent(field)}` : undefined;
  type PlateRow = { label: string; value: string | null; mono?: boolean; field?: string };
  const year = mould.manufactureYear ? String(mould.manufactureYear) : null;
  const name = hasOwnName(mould) ? mould.name : null;
  // What is engraved, row for row, in the plate's own words.
  const plateRows: PlateRow[] =
    design === DESIGN_A_CODE
      ? [
          { label: 'MOLD ID', value: mould.assetCode, mono: true },
          { label: 'MOLD NAME', value: name, field: 'name' },
          {
            label: 'CAVITY/RUNNER',
            value: [mould.cavities, mould.runnerTypeName].filter(Boolean).join(' - ') || null,
            field: 'cavities',
          },
          { label: 'MOLD DIMENSION', value: mould.moldSize, field: 'moldSize' },
          { label: 'MANUFACTURING YEAR', value: year, field: 'manufactureYear' },
        ]
      : [
          // Design B's five rows, in the client's order (11 Oct 2026).
          { label: 'Mold ID', value: mould.assetCode, mono: true },
          { label: 'Cavity', value: mould.cavities, field: 'cavities' },
          { label: 'Mold Name', value: name, field: 'name' },
          { label: 'Mold Dimension', value: mould.moldSize, field: 'moldSize' },
          { label: 'Property Ownership', value: ownerName, field: 'ownerCompanyId' },
        ];
  const stillBlank = plateRows.filter((r) => !r.value).map((r) => r.label);

  return (
    <Page
      title={mould.assetCode}
      description={hasOwnName(mould) ? mould.name : 'No name recorded in the source data'}
      back={{ to: '/moulds', label: 'Back to catalog' }}
      actions={
        <>
          <Button variant="secondary" onClick={() => setShowLabel(true)}>
            <QrCode className="size-4" /> Label
          </Button>
          {canEdit ? (
            <>
              {/* A family of similar tools differs in two or three fields.
                  Retyping the other twenty is how the second one ends up with a
                  different core material than the first by accident. */}
              <Button variant="secondary" asChild>
                <Link to="/moulds/new" state={{ cloneOf: mould.id }}>
                  <Copy className="size-4" /> Duplicate
                </Link>
              </Button>
              <Button variant="primary" asChild>
                <Link to={`/moulds/${encodeURIComponent(mould.assetCode)}/edit`}>
                  <Pencil className="size-4" /> Edit
                </Link>
              </Button>
            </>
          ) : null}
          {canDelete ? (
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete mould">
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </>
      }
    >
      {/* --- identity --- */}
      <Card className="mb-4">
        <CardBody className="flex flex-col gap-5 sm:flex-row sm:items-start">
          {mould.photoUrl ? (
            <button
              type="button"
              onClick={() => setOpenPhotos((n) => n + 1)}
              className="relative block h-40 w-full shrink-0 overflow-hidden rounded-lg border border-border sm:w-40"
              aria-label={`Open the photos${(mould.photoCount ?? 0) > 1 ? ` (${mould.photoCount})` : ''}`}
            >
              <img src={mould.photoUrl} alt={mould.name} className="size-full object-cover" />
              {(mould.photoCount ?? 0) > 1 ? (
                <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/65 px-2 py-0.5 text-xs font-medium text-white tabular">
                  1 / {mould.photoCount}
                </span>
              ) : null}
            </button>
          ) : (
            <div className="grid h-40 w-full place-items-center rounded-lg bg-surface-sunken text-subtle-foreground sm:w-40">
              <div className="flex flex-col items-center gap-1 text-xs">
                <ImageOff className="size-5" aria-hidden />
                No photo
              </div>
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Code className="text-lg font-bold">{mould.assetCode}</Code>
              {/* Its status, from the scan ledger: a Mold OUT makes it In transit
                  until the Mold IN at its destination. */}
              {mould.status ? (
                <Badge tone={STATUS_TONE[mould.status] ?? 'neutral'} data-testid="mould-status">
                  {mouldStatusLabel(mould.status)}
                </Badge>
              ) : null}
              {mould._pending === 1 ? (
                <Tooltip content="Saved on this device - will sync when you are online">
                  <Badge tone="warning">
                    <CloudUpload className="size-3" /> Not synced
                  </Badge>
                </Tooltip>
              ) : null}
              {mould.isActive === false ? <Badge tone="neutral">Inactive</Badge> : null}
            </div>
            {hasOwnName(mould) ? (
              <p className="mt-1 text-lg font-medium">{mould.name}</p>
            ) : (
              <p className="mt-1 text-sm text-subtle-foreground">No name recorded</p>
            )}
            {mould.description ? (
              <p className="mt-2 text-sm text-muted-foreground">{mould.description}</p>
            ) : canEdit ? (
              <p className="mt-2 text-xs text-subtle-foreground">
                No notes ·{' '}
                <Link to={addHref('description')!} className="font-medium text-primary underline-offset-2 hover:underline">
                  Add
                </Link>
              </p>
            ) : null}

            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Pair label="Project" value={mould.projectName} addHref={addHref('projectId')} />
              <Pair label="QR number" value={mould.qrNumber ?? null} mono addHref={addHref('qrNumber')} />
              <Pair label="Owner" value={mould.ownerCompanyName} addHref={addHref('ownerCompanyId')} />
              <Pair label="Customer" value={mould.customerCompanyName} addHref={addHref('customerCompanyId')} />
              <Pair label="Location" value={where.label ?? 'Not placed yet'} wrap />
              <Pair
                label="Manufacturing year"
                value={mould.manufactureYear ? String(mould.manufactureYear) : null}
                addHref={addHref('manufactureYear')}
              />
              <Pair
                label="Manufactured by"
                value={mould.manufactureCompanyName}
                addHref={addHref('manufactureCompanyId')}
              />
            </dl>
          </div>

          {/* Bigger than it was, and a button through to a printable one: the old
              104-pixel square was readable by eye and not by a phone. */}
          <button
            type="button"
            onClick={() => setShowLabel(true)}
            className="flex shrink-0 flex-col items-center gap-1.5 rounded-lg p-1 hover:bg-surface-sunken"
            aria-label="Open the printable label"
          >
            <div className="rounded-lg border border-border bg-white p-2">
              <QRCodeSVG value={mouldQrValue(mould)} level="M" size={148} bgColor="#ffffff" fgColor="#000000" />
            </div>
            <p className="text-[11px] text-muted-foreground">Print or enlarge label</p>
          </button>
        </CardBody>
      </Card>

      <MouldPhotoGallery
        mouldId={mould.id}
        name={hasOwnName(mould) ? mould.name : mould.assetCode}
        canEdit={canEdit}
        openSignal={openPhotos}
      />

      {/* --- custody ---
          Above the specification because "who has it" is the question somebody
          opens this page to answer far more often than "what gauge steel". */}
      {mould.issuedToUserId || mould.expectedBackBy || where.inTransit ? (
        <Card
          className={`mb-4 ${
            mould.expectedBackBy && new Date(mould.expectedBackBy).getTime() < Date.now()
              ? 'border-danger/40'
              : ''
          }`}
        >
          <CardBody className="flex items-start gap-3">
            <PackageOpen className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="text-sm">
              <p className="font-medium" data-testid="custody-title">
                {where.inTransit ? 'In transit' : mould.issuedToUserId ? 'Checked out' : 'Due back'}
                {mould.expectedBackBy && new Date(mould.expectedBackBy).getTime() < Date.now() ? (
                  <span className="ml-2 text-danger">overdue</span>
                ) : null}
              </p>
              <p className="text-muted-foreground">
                {mould.issuedAt
                  ? `${where.inTransit ? `Checked out${where.leftFrom ? ` at ${where.leftFrom}` : ''} on` : 'Taken'} ${new Date(mould.issuedAt).toLocaleDateString()}`
                  : null}
                {mould.issuedAt && mould.expectedBackBy ? ' · ' : null}
                {mould.expectedBackBy
                  ? `expected back ${new Date(mould.expectedBackBy).toLocaleDateString()}`
                  : null}
              </p>
              {where.inTransit ? (
                <p className="mt-1 text-xs text-subtle-foreground">
                  It stays in transit until it is checked in at its destination.
                </p>
              ) : null}
              {/* The holder's name is not replicated to this device - the roster
                  is not a sync table - so the board is where you see who. */}
              {mould.issuedToUserId ? (
                <p className="mt-1 text-xs text-subtle-foreground">See Tracking for who is holding it.</p>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* --- journey ---
          Where it is going, when it is going anywhere. The status badge says
          IN_TRANSIT; this says between which two places. */}
      <MouldJourneyCard mouldId={mould.id} />

      {/* --- usage ---
          A mould's life is measured in shots, not years. This is the number
          that drives servicing, quoting and replacement planning. */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="size-4 text-muted-foreground" aria-hidden />
            Usage
          </CardTitle>
          {canRecordShots ? (
            <Button variant="secondary" size="sm" onClick={() => setRecordingShots(true)}>
              Record reading
            </Button>
          ) : null}
        </CardHeader>
        <CardBody>
          {mould.shotCount != null ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular">
                  {mould.shotCount.toLocaleString()}
                </span>
                <span className="text-sm text-muted-foreground">
                  shots{mould.ratedLife ? ` of ${mould.ratedLife.toLocaleString()} rated` : ''}
                </span>
              </div>
              {mould.ratedLife ? (
                <>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className={
                        mould.shotCount / mould.ratedLife > 0.9
                          ? 'h-full rounded-full bg-danger'
                          : mould.shotCount / mould.ratedLife > 0.75
                            ? 'h-full rounded-full bg-warning'
                            : 'h-full rounded-full bg-primary'
                      }
                      style={{
                        width: `${Math.min(100, (mould.shotCount / mould.ratedLife) * 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {Math.round((mould.shotCount / mould.ratedLife) * 100)}% of rated life used
                  </p>
                </>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No shot readings yet. Record one to start tracking how much life this tool has used.
            </p>
          )}
        </CardBody>
      </Card>

      {/* --- what gets engraved --- */}
      <Card className="mb-4 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stamp className="size-4 text-primary" aria-hidden />
            Engraved on the nameplate
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Permanent once cut</span>
            {/* The renderer has existed since v2 with nothing calling it. DXF is
                what the engraver's machine reads; SVG is the proof you check
                before cutting metal you cannot uncut. */}
            {canExportNameplate ? (
              <Menu
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={engraving !== null}
                  >
                    <Stamp className="size-3.5" /> Export
                  </Button>
                }
              >
                {offeredDesigns.length > 1 ? (
                  <>
                    <MenuLabel>Nameplate</MenuLabel>
                    <MenuItem onSelect={() => setComparing(true)}>
                      Compare the designs
                    </MenuItem>
                    <MenuSeparator />
                  </>
                ) : null}
                <MenuLabel>{DESIGN_LABEL[design] ?? 'Design B'}</MenuLabel>
                <MenuItem onSelect={() => void downloadNameplate('svg')}>
                  SVG - proof to check first
                </MenuItem>
                <MenuItem onSelect={() => void downloadNameplate('dxf')}>
                  DXF - for the engraving machine
                </MenuItem>
              </Menu>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          {engraveError ? (
            <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
              {engraveError}
            </p>
          ) : null}
          {/* Whether the engrave file was already downloaded - so a plate is not
              cut twice by mistake. Export downloads it again at any time. */}
          {(() => {
            const d = engraveDownloads[mould.id];
            return d ? (
              <p className="mb-3 flex items-start gap-2 rounded-lg bg-success-subtle px-3 py-2 text-sm" data-testid="engrave-status" title={describeDownload(d)}>
                <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                <span>
                  Engrave file downloaded {downloadDate(d.lastAt)}
                  {d.lastBy ? ` by ${d.lastBy}` : ''}
                  {d.count > 1 ? ` · ${d.count} times` : ''}.{' '}
                  <span className="text-muted-foreground">Export downloads it again.</span>
                </span>
              </p>
            ) : (
              <p className="mb-3 text-sm text-muted-foreground" data-testid="engrave-status">
                Engrave file not downloaded yet.
              </p>
            );
          })()}
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {plateRows.map((r) => (
              <Pair
                key={r.label}
                label={r.label}
                value={r.value}
                mono={r.mono}
                addHref={r.field ? addHref(r.field) : undefined}
              />
            ))}
          </dl>
          {stillBlank.length ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Still blank: {stillBlank.join(', ').toLowerCase()}. The plate can still be made - every label is
              engraved, with nothing after it until someone fills it in.
            </p>
          ) : null}
        </CardBody>
      </Card>

      {/* --- full specification --- */}
      <div className="grid gap-4 lg:grid-cols-2">
        {MOULD_SECTIONS.filter((s) => s.id !== 'identity' && s.id !== 'output').map((section) => (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
            </CardHeader>
            <CardBody>
              <dl className="grid gap-x-6 gap-y-2 text-sm">
                {section.fields
                  .filter((f) => f.kind !== 'photo' && f.kind !== 'textarea')
                  .map((field) => (
                    <Pair
                      key={field.name}
                      label={field.label}
                      value={readField(mould as unknown as Record<string, unknown>, field)}
                      addHref={addHref(field.name)}
                    />
                  ))}
              </dl>
            </CardBody>
          </Card>
        ))}
      </div>

      <MouldImports mouldId={mould.id} />

      <MouldAuditTrail mould={mould} />

      <MouldHistory mouldId={mould.id} />

      <MouldLabelDialog
        mouldId={mould.id}
        assetCode={mould.assetCode}
        name={mould.name}
        owner={mould.ownerCompanyName}
        open={showLabel}
        onOpenChange={setShowLabel}
      />

      {recordingShots && mould ? (
        <ShotReadingDialog
          mould={mould}
          open
          onOpenChange={setRecordingShots}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        destructive
        title={`Delete ${mould.assetCode}?`}
        description="The mould is removed from the catalog. Any scan history it already has is kept."
        confirmLabel="Delete mould"
        loading={deleting}
        onConfirm={() => {
          setDeleting(true);
          void deleteRow('moulds', mould.id)
            .then(() => navigate('/moulds'))
            .finally(() => setDeleting(false));
        }}
      />

      {comparing ? (
        <NameplateCompareDialog
          assetCode={mould.assetCode}
          current={design}
          onChoose={async (chosen) => {
            setDesign(chosen.code);
            // Written to the record only now, so the next person to open this
            // tool gets the plate somebody actually chose for it - and the
            // change lands in the audit trail like any other edit.
            if (chosen.templateId) {
              await updateRow('moulds', mould.id, { nameplateTemplateId: chosen.templateId });
            }
          }}
          onClose={() => setComparing(false)}
        />
      ) : null}
    </Page>
  );
}

function readField(row: Record<string, unknown>, field: FieldDef): string | null {
  const value = row[displayKey(field)] ?? row[field.name];
  if (value === null || value === undefined || value === '') return null;
  if (field.kind === 'date') return formatDate(String(value));
  if (field.kind === 'yesno') return value === true || value === 'true' ? 'Yes' : 'No';
  return String(value);
}

function Pair({
  label,
  value,
  mono,
  addHref,
  wrap,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
  /** Where to fill it in, when it is blank and the viewer may edit. */
  addHref?: string;
  /** Show all of it, over two lines if need be ("In transit · from Mira Road"), rather than cut it short. */
  wrap?: boolean;
}) {
  const blank = value === null || value === undefined || value === '';
  const fit = wrap ? 'min-w-0 text-right' : 'truncate';
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 border-b border-border/60 py-1 last:border-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className={mono ? `${fit} font-mono text-[13px] font-medium` : `${fit} font-medium`}>
        {blank ? (
          <span className="font-normal text-subtle-foreground">
            Not set
            {addHref ? (
              <>
                {' · '}
                <Link to={addHref} className="font-medium text-primary underline-offset-2 hover:underline">
                  Add
                </Link>
              </>
            ) : null}
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <Page title="Loading…" back={{ to: '/moulds', label: 'Back to catalog' }}>
      <Card className="mb-4">
        <CardBody className="flex flex-col gap-5 sm:flex-row">
          <Skeleton className="h-40 w-full sm:w-40" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-5 w-64" />
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
        </CardBody>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <CardBody className="space-y-2">
              {[0, 1, 2].map((j) => (
                <Skeleton key={j} className="h-4 w-full" />
              ))}
            </CardBody>
          </Card>
        ))}
      </div>
    </Page>
  );
}
